/**
 * ============================================================================
 * SPINNER COMPONENT - 100% Ready with Multiple Variants
 * ============================================================================
 */

import React from 'react';

/**
 * Spinner Component
 * @param {Object} props
 * @param {string} props.size - Size (sm, md, lg, xl)
 * @param {string} props.variant - Variant (default, dots, pulse, bars)
 * @param {string} props.color - Color (blue, green, red, gray)
 * @param {string} props.className - Additional classes
 * @param {string} props.label - Accessibility label
 * @returns {JSX.Element}
 */
const Spinner = ({ 
  size = 'md', 
  variant = 'default',
  color = 'blue',
  className = '',
  label = 'Loading...'
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  };

  const colorClasses = {
    blue: 'text-blue-600 dark:text-blue-400',
    green: 'text-green-600 dark:text-green-400',
    red: 'text-red-600 dark:text-red-400',
    gray: 'text-gray-600 dark:text-gray-400',
  };

  // Default spinner (circular)
  if (variant === 'default') {
    return (
      <div className={`inline-block ${className}`} role="status" aria-label={label}>
        <svg
          className={`animate-spin ${sizeClasses[size] || sizeClasses.md} ${colorClasses[color] || colorClasses.blue}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span className="sr-only">{label}</span>
      </div>
    );
  }

  // Dots variant
  if (variant === 'dots') {
    return (
      <div className={`inline-flex gap-1 ${className}`} role="status" aria-label={label}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`
              ${sizeClasses[size] || sizeClasses.md}
              rounded-full ${colorClasses[color] || colorClasses.blue}
              animate-pulse
            `}
            style={{
              animationDelay: `${i * 0.2}s`,
              backgroundColor: 'currentColor',
            }}
          />
        ))}
        <span className="sr-only">{label}</span>
      </div>
    );
  }

  // Pulse variant
  if (variant === 'pulse') {
    return (
      <div className={`inline-block ${className}`} role="status" aria-label={label}>
        <div
          className={`
            ${sizeClasses[size] || sizeClasses.md}
            rounded-full ${colorClasses[color] || colorClasses.blue}
            animate-pulse
          `}
          style={{ backgroundColor: 'currentColor' }}
        />
        <span className="sr-only">{label}</span>
      </div>
    );
  }

  // Bars variant
  if (variant === 'bars') {
    return (
      <div className={`inline-flex gap-1 items-end ${className}`} role="status" aria-label={label}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`
              w-1 ${colorClasses[color] || colorClasses.blue}
              animate-pulse
            `}
            style={{
              height: `${(i % 2 === 0 ? 0.6 : 1) * 100}%`,
              animationDelay: `${i * 0.1}s`,
              backgroundColor: 'currentColor',
            }}
          />
        ))}
        <span className="sr-only">{label}</span>
      </div>
    );
  }

  return null;
};

export default Spinner;
